import React, { useState, useRef } from 'react';
import {
    CheckCircle,
    XCircle,
    AlertCircle,
    Clock,
    CheckCircle2,
    ArrowLeft,
    ArrowRight,
    RotateCcw,
    Layout,
    ArrowLeft as BackIcon
} from 'lucide-react';
import axios from 'axios';
import { showError, showSuccess, showConfirm } from '../../../utils/Alert';
import './AssessmentEngine.css';

const AssessmentEngine = ({ assessment, onComplete, onExit }) => {
    const [currentQuestionIdx, setCurrentQuestionIdx] = useState(0);
    const [answers, setAnswers] = useState({});
    const [submissionFile, setSubmissionFile] = useState(null);
    const [submissionText, setSubmissionText] = useState('');
    const [result, setResult] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [timeLeft, setTimeLeft] = useState((assessment.timeLimit || 20) * 60); // seconds
    const timerRef = useRef(null);
    const isExam = assessment.contentType === 'Exam';
    const isMCQ = assessment.contentType === 'Quiz' || assessment.contentType === 'Exam';

    React.useEffect(() => {
        if (isExam && !result) {
            timerRef.current = setInterval(() => {
                setTimeLeft(prev => {
                    if (prev <= 1) {
                        clearInterval(timerRef.current);
                        autoSubmit();
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        }
        return () => clearInterval(timerRef.current);
    }, [isExam, result]);

    const autoSubmit = () => {
        showError('Time Expired', 'Your exam session has ended. Submitting current responses...');
        handleSubmit();
    };

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const handleStrictExit = async () => {
        const warningTitle = isExam ? 'Exit and Submit?' : 'Exit Assessment?';
        const warningText = isExam
            ? 'If you close now, your exam will be automatically submitted with current answers. Proceed?'
            : 'Are you sure you want to exit? Your progress may not be saved.';

        const confirmation = await showConfirm(warningTitle, warningText, 'Yes, Exit');

        if (confirmation.isConfirmed) {
            if (isExam && !result) {
                await handleSubmit();
            } else {
                onExit();
            }
        }
    };

    const handleOptionSelect = (questionId, option) => {
        setAnswers(prev => ({ ...prev, [questionId]: option }));
    };

    const handleSubmit = async () => {
        if (isSubmitting) return;
        setIsSubmitting(true);
        if (timerRef.current) clearInterval(timerRef.current);

        try {
            const token = localStorage.getItem('token');
            const data = new FormData();
            data.append('assessmentId', assessment._id);

            if (isMCQ) {
                const formattedAnswers = Object.keys(answers).map(qId => ({
                    questionId: qId,
                    selectedOption: String(answers[qId]).trim()
                }));
                data.append('answers', JSON.stringify(formattedAnswers));
            } else {
                if (submissionFile) data.append('submissionFile', submissionFile);
                if (submissionText) data.append('submissionText', submissionText);
            }

            const res = await axios.post('/api/student/submissions', data, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data'
                }
            });

            setResult(res.data.submission);
            const score = (res.data.submission.obtainedMarks / res.data.submission.totalMarks) * 100;
            const passing = assessment.passingPercentage || 80;

            if (score >= passing) {
                showSuccess('Excellent!', `You passed with ${score.toFixed(0)}%`);
                await axios.post('/api/student/progress/complete', {
                    courseId: assessment.courseId._id || assessment.courseId,
                    lessonId: assessment._id
                }, {
                    headers: { Authorization: `Bearer ${token}` }
                });
            } else if (isMCQ) {
                showError('Assessment Failed', `Your score: ${score.toFixed(0)}%. Passing: ${passing}%`);
            } else {
                showSuccess('Submitted', 'Your assignment has been received and is pending review.');
            }
        } catch (err) {
            showError('Submission Error', 'Failed to save assessment. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (result) {
        const score = (result.obtainedMarks / result.totalMarks) * 100;
        const passed = score >= (assessment.passingPercentage || 80);
        const isPending = result.status === 'Pending';

        return (
            <div className="engine-container result-view">
                <div className="portal-header">
                    <button className="back-btn" onClick={onExit}>
                        <BackIcon size={20} />
                        <span>Exit Portal</span>
                    </button>
                    <h2>{assessment.title} - {isPending ? 'Submission Received' : 'Results'}</h2>
                </div>

                <div className="result-card-premium fade-in">
                    <div className={`status-icon-large ${isPending ? 'info' : (passed ? 'success' : 'fail')}`}>
                        {isPending ? <Layout size={80} /> : (passed ? <CheckCircle size={80} /> : <XCircle size={80} />)}
                    </div>

                    <h2 className="result-headline">
                        {isPending ? 'Work Received!' : (passed ? 'Congratulations!' : 'Keep Practicing')}
                    </h2>
                    <p className="result-subtext">
                        {isPending
                            ? 'Your assignment has been successfully submitted and is now waiting for review by your tutor.'
                            : (passed ? 'You have successfully cleared this assessment.' : 'You didn\'t meet the passing criteria this time.')}
                    </p>

                    {!isPending && (
                        <>
                            <div className="score-ring">
                                <span className="score-percent">{score.toFixed(0)}%</span>
                                <span className="score-label">Final Score</span>
                            </div>

                            <div className="stats-grid">
                                <div className="stat-item">
                                    <span>Points Earned</span>
                                    <strong>{result.obtainedMarks} / {result.totalMarks}</strong>
                                </div>
                                <div className="stat-item">
                                    <span>Status</span>
                                    <strong className={passed ? 'txt-success' : 'txt-fail'}>{passed ? 'PASSED' : 'FAILED'}</strong>
                                </div>
                            </div>
                        </>
                    )}

                    <div className="result-actions">
                        <button className="primary-engine-btn" onClick={onExit}>Return to Dashboard</button>
                        {!passed && !isPending && (
                            <button className="secondary-engine-btn" onClick={() => { setResult(null); setCurrentQuestionIdx(0); setAnswers({}); }}>
                                <RotateCcw size={18} /> Retry Assessment
                            </button>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    const currentQuestion = assessment.questions[currentQuestionIdx];

    return (
        <div className="engine-container">
            <header className="portal-header">
                <div className="header-left">
                    <button className="exit-btn" onClick={handleStrictExit} title="Exit and submit">
                        <XCircle size={20} />
                    </button>
                    <div className="brand-info">
                        <span className="type-badge">{assessment.contentType}</span>
                        <h1>{assessment.title}</h1>
                    </div>
                </div>
                <div className="header-right">
                    <div className={`timer-box ${isExam && timeLeft < 60 ? 'warning' : ''}`}>
                        <Clock size={16} />
                        <span>{isExam ? formatTime(timeLeft) : `${assessment.timeLimit || 20}m Limit`}</span>
                    </div>
                </div>
            </header>

            <main className="engine-main">
                {isMCQ ? (
                    <>
                        <div className="quiz-stepper">
                            <div className="stepper-track">
                                <div
                                    className="stepper-fill"
                                    style={{ width: `${((currentQuestionIdx + 1) / assessment.questions.length) * 100}%` }}
                                ></div>
                            </div>
                            <span className="step-count">Question {currentQuestionIdx + 1} of {assessment.questions.length}</span>
                        </div>

                        <div className="question-theater">
                            <div className="question-card-inner">
                                <h3 className="question-text">{assessment.questions[currentQuestionIdx]?.questionText}</h3>

                                <div className="options-layout">
                                    {(assessment.questions[currentQuestionIdx]?.options || []).map((opt, idx) => {
                                        const qId = assessment.questions[currentQuestionIdx]._id;
                                        const isSelected = answers[qId] === opt;
                                        return (
                                            <div
                                                key={idx}
                                                className={`option-choice ${isSelected ? 'selected' : ''}`}
                                                onClick={() => handleOptionSelect(qId, opt)}
                                            >
                                                <div className="choice-marker">{String.fromCharCode(65 + idx)}</div>
                                                <span className="choice-text">{opt}</span>
                                                {isSelected && <CheckCircle2 className="choice-check" size={20} />}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </>
                ) : (
                    <div className="assignment-portal-view fade-in">
                        <div className="instruction-card">
                            <div className="card-header-icon"><Layout size={24} /></div>
                            <h3>Submission Instructions</h3>
                            <div className="instructions-content">
                                {assessment.instructions || "Please complete the assignment according to the requirements and upload your files for review."}
                            </div>
                        </div>

                        <div className="submission-zone">
                            <div className="form-group">
                                <label className="engine-label">Additional Comments / Notes</label>
                                <textarea
                                    className="engine-textarea"
                                    placeholder="Write any notes for the instructor here..."
                                    value={submissionText}
                                    onChange={(e) => setSubmissionText(e.target.value)}
                                />
                            </div>

                            <div className="file-upload-block">
                                <label className="engine-label">Upload Submission File</label>
                                <div className="custom-file-input">
                                    <input
                                        type="file"
                                        id="sub-file"
                                        onChange={(e) => setSubmissionFile(e.target.files[0])}
                                    />
                                    <label htmlFor="sub-file" className="file-trigger">
                                        <CheckCircle2 size={18} />
                                        <span>{submissionFile ? submissionFile.name : 'Choose file...'}</span>
                                    </label>
                                </div>
                                <p className="file-hint">Accepted formats: PDF, DOCX, ZIP (Max 50MB)</p>
                            </div>
                        </div>
                    </div>
                )}
            </main>

            <footer className="engine-footer">
                <div className="footer-content">
                    {isMCQ ? (
                        <>
                            <button
                                className="nav-btn prev"
                                onClick={() => setCurrentQuestionIdx(p => p - 1)}
                                disabled={currentQuestionIdx === 0}
                            >
                                <ArrowLeft size={18} /> Previous
                            </button>

                            <div className="quick-jump">
                                {assessment.questions.map((_, i) => (
                                    <div
                                        key={i}
                                        className={`jump-dot ${i === currentQuestionIdx ? 'active' : ''} ${answers[assessment.questions[i]._id] ? 'answered' : ''}`}
                                        onClick={() => setCurrentQuestionIdx(i)}
                                    />
                                ))}
                            </div>

                            {currentQuestionIdx === assessment.questions.length - 1 ? (
                                <button
                                    className="submit-engine-btn"
                                    onClick={handleSubmit}
                                    disabled={isSubmitting || Object.keys(answers).length < assessment.questions.length}
                                >
                                    {isSubmitting ? 'Submitting...' : 'Finish Assessment'}
                                </button>
                            ) : (
                                <button className="nav-btn next" onClick={() => setCurrentQuestionIdx(p => p + 1)}>
                                    Next Question <ArrowRight size={18} />
                                </button>
                            )}
                        </>
                    ) : (
                        <div style={{ width: '100%', display: 'flex', justifyContent: 'flex-end' }}>
                            <button
                                className="submit-engine-btn"
                                onClick={handleSubmit}
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? 'Sending Submission...' : 'Secure Submit Assignment'}
                            </button>
                        </div>
                    )}
                </div>
            </footer>
        </div>
    );
};

export default AssessmentEngine;
